import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useLocale } from 'next-intl'
import { fetchClient, userApiClient } from '@/setup/api'
import {
    DEFAULT_RESTAURANT_PAGE,
    DEFAULT_RESTAURANT_SORTING,
    RestaurantQuerySorting,
} from '@/setup/urls/city-route'
import { compact } from '@/utils/functions/compact'

export type IGetRestaurantsQueryParams = {
    citySlug?: string | null
    name?: string | null
    brandSlug?: string | null
    tagSlugs?: string[] | null | string
    sortBy?: RestaurantQuerySorting | null
    page?: number | null
    openNow?: boolean | null
    lat?: string
    lng?: string
}

/**
 * The url param is sometimes an array and sometimes a single comma-joined value,
 * so normalise it to an array.
 */
export const prepareTagSlugs = (tagSlugs: IGetRestaurantsQueryParams['tagSlugs']) => {
    if (!tagSlugs) return null

    return Array.isArray(tagSlugs) ? compact(tagSlugs) : tagSlugs.split(',')
}

/**
 * Defaults are omitted rather than sent explicitly, so equivalent searches produce
 * one url and therefore one Vercel CDN cache entry.
 */
const preparePage = (page: number | null | undefined) =>
    !page || page === DEFAULT_RESTAURANT_PAGE ? undefined : page

const prepareQuery = (params: Omit<IGetRestaurantsQueryParams, 'citySlug'>) => {
    const preparedTagSlugs = prepareTagSlugs(params.tagSlugs)

    return {
        name: params.name ?? undefined,
        brandSlug: params.brandSlug ?? undefined,
        // The api reads this as one comma-joined string, not as repeated keys.
        tagSlugs: preparedTagSlugs?.length ? preparedTagSlugs.join(',') : undefined,
        sortBy:
            !params.sortBy || params.sortBy === DEFAULT_RESTAURANT_SORTING
                ? undefined
                : params.sortBy,
        openNow: params.openNow === true ? ('true' as const) : undefined,
        lat: params.lat ?? undefined,
        lng: params.lng ?? undefined,
        // Loyalty is always wanted in restaurant results.
        includeLoyalty: 'true' as const,
        page: preparePage(params.page),
    }
}

const buildInit = (params: IGetRestaurantsQueryParams, locale: string) => ({
    params: {
        path: { citySlug: params.citySlug ?? '' },
        query: prepareQuery(params),
    },
    headers: { 'accept-language': locale },
})

export const getRestaurantsQueryOptions = (params: IGetRestaurantsQueryParams, locale: string) =>
    userApiClient.queryOptions(
        'get',
        '/web/restaurants/cities/{citySlug}/restaurants',
        buildInit(params, locale),
        // The city slug is a path segment, so there is nothing to fetch without it.
        // Ignored by `fetchQuery`, which is what the server prefetches use.
        { enabled: !!params.citySlug }
    )

export function useGetRestaurantsQuery(params: IGetRestaurantsQueryParams) {
    const locale = useLocale()

    return useQuery(getRestaurantsQueryOptions(params, locale))
}

const getNextPageParam = (
    lastPage: { data: unknown[]; meta: { page: number; isLastPage: boolean } } | undefined
) => {
    if (!lastPage) return DEFAULT_RESTAURANT_PAGE

    // Without this the list fetches one guaranteed-empty page to discover the end.
    if (lastPage.meta.isLastPage) return null

    if (lastPage.data.length === 0) return null

    return Number(lastPage.meta.page) + 1
}

/**
 * openapi-react-query ships `queryOptions` but no `infiniteQueryOptions`, so the key
 * and the page-param injection its `useInfiniteQuery` does internally are reproduced
 * here once, and shared by the hook and the server prefetch. Writing them twice would
 * let the dehydrated pages land on a different key than the hook reads.
 */
export const getRestaurantsInfiniteQueryOptions = (
    params: Omit<IGetRestaurantsQueryParams, 'page'>,
    locale: string
) => {
    const init = buildInit(params, locale)

    const { queryKey } = userApiClient.queryOptions(
        'get',
        '/web/restaurants/cities/{citySlug}/restaurants',
        init
    )

    return {
        // Must not collide with `getRestaurantsQueryOptions` on the same path: infinite
        // queries cache `{ pages, pageParams }` while a plain query caches the bare
        // envelope, so sharing a key makes `data.pages` undefined and react-query
        // throws on `pages.length`. Safe to append because the queryFn below closes
        // over `init` rather than reading it back off the key.
        // https://github.com/TanStack/query/discussions/7455#discussioncomment-9481854
        queryKey: [...queryKey, 'infinite'],
        initialPageParam: DEFAULT_RESTAURANT_PAGE,
        getNextPageParam,
        queryFn: async ({ pageParam, signal }: { pageParam: number; signal: AbortSignal }) => {
            const { data, error } = await fetchClient.GET(
                '/web/restaurants/cities/{citySlug}/restaurants',
                {
                    ...init,
                    signal,
                    params: {
                        ...init.params,
                        query: { ...init.params.query, page: pageParam },
                    },
                }
            )

            if (error) throw error

            return data
        },
    }
}

export function useGetRestaurantsInfiniteQuery(params: Omit<IGetRestaurantsQueryParams, 'page'>) {
    const locale = useLocale()

    return useInfiniteQuery({
        ...getRestaurantsInfiniteQueryOptions(params, locale),
        placeholderData: (previousPage) => previousPage,
    })
}
